import { ActionTree } from 'vuex';
import router from '../../../router/index';
import ApiUrl from '../../../api/url';
import { UserState, NewUser, EditUser, PersonalSettings, UpdatePassword } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import { isHappenedOtherPartyError } from '../../error-type';
import ScreenUrl from '../../../router/url';

export const actions: ActionTree<UserState, RootState> = {
    /**
     * ユーザー一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} word 検索キーワード
     * @param {any} ignoreGroupId 除外するグループID
     * @param {any} ignoreUserIds 除外するユーザーID
     * @param {any} userGroupIds 対象にするグループID
     * @param {any} provider 検索対象の認証プロバイダ
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @returns {Promise<void>}
     */
    async search(
        { dispatch, commit },
        {
            word = '',
            ignoreGroupId = undefined,
            ignoreUserIds = undefined,
            userGroupIds = undefined,
            provider = '',
            pageNumber,
            perPage,
            sort,
        } = {}
    ) {
        const payload = {
            word: word,
            ignore_group_id: ignoreGroupId,
            ignore_user_ids: ignoreUserIds,
            user_group_ids: userGroupIds,
            provider: provider,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.User.SEARCH, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ユーザー新規作成前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async new({ commit }) {
        commit(types.NEW_USER);
    },

    /**
     * ユーザー新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewUserGroup} newUser 新規ユーザー情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newUser: NewUser) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.User.CREATE, params: newUser },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.CREATED);
                dispatch('toastMessage', `${newUser.uid} ${message}`, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * ユーザーの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id ユーザーのid（主キー）
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.User.SHOW.replace(':id', id.toString()) },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ユーザー更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditUser} editUser 更新するユーザー情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState, rootGetters }, editUser: EditUser) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.User.UPDATE.replace(':id', editUser.id.toString()),
                    params: editUser,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 自分の別名を更新した場合は、認証ユーザー情報を上書く
                if (rootGetters['auth/id']() === editUser.id && 'name' in editUser) {
                    await dispatch('auth/overwriteUser', { name: editUser.name }, { root: true });
                }

                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * ユーザー削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id 削除するユーザーのid（主キー）
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.User.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * ユーザーロック解除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ユーザーのid（主キー）
     * @returns {Promise<void>}
     */
    async unlock({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.UNLOCK_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.User.UNLOCK, params: { id } },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UNLOCK_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.UNLOCKED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UNLOCK_FAILURE, params);
        }
    },

    /**
     * ユーザー失効
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ユーザーのid（主キー）
     * @param {boolean} expired 変更したい失効状態（True:失効状態,False:有効状態）
     * @returns {Promise<void>}
     */
    async expire({ dispatch, commit, rootState }, payload: { id: number; expired: boolean }) {
        try {
            commit(types.EXPIRE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.User.EXPIRE.replace(':id', payload.id.toString()),
                    params: { expired: payload.expired },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.EXPIRE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.EXPIRED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.EXPIRE_FAILURE, params);
        }
    },

    /**
     * パスワード更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditUser} editUser 更新するユーザー情報
     * @returns {Promise<void>}
     */
    async updatePassword({ dispatch, commit, rootState }, editUser: UpdatePassword) {
        try {
            commit(types.UPDATE_PASSWORD_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.User.UPDATE_PASSWORD.replace(':id', editUser.id.toString()),
                    params: editUser,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_PASSWORD_SUCCESS);

                router.push(ScreenUrl.TOP);
                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.PASSWORD_UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_PASSWORD_FAILURE, params);
        }
    },

    /**
     * パスワード変更に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async passwordErrorReset({ commit }) {
        commit(types.PASSWORD_ERROR_RESET);
    },

    /**
     * 多要素認証設定更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ユーザーのid（主キー）
     * @param {boolean} multifactor_authentication_enabled 変更したい状態（True:有効,False:無効）
     * @returns {Promise<void>}
     */
    async multifactor_auth(
        { dispatch, commit, rootState },
        payload: { id: number; multifactor_authentication_enabled: boolean }
    ) {
        try {
            commit(types.MULTIFACTOR_AUTH_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.User.MULTIFACTOR_AUTHENTICATION_ENABLED.replace(':id', payload.id.toString()),
                    params: { multifactor_authentication_enabled: payload.multifactor_authentication_enabled },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.MULTIFACTOR_AUTH_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.MULTIFACTOR_AUTH);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.MULTIFACTOR_AUTH_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} word 検索キーワード
     * @param {any} ignoreGroupId 除外するグループID
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { word = '', ignoreGroupId = undefined, sort } = {}) {
        const payload = {
            word: word,
            ignore_group_id: ignoreGroupId,
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.User.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                router.push(ScreenUrl.User.INDEX);
                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 個人設定取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async show_personal_settings({ dispatch, commit, rootState }) {
        try {
            commit(types.SHOW_PERSONAL_SETTINGS_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.User.PERSONAL_SETTINGS },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_PERSONAL_SETTINGS_OUTPUT_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.SHOW_PERSONAL_SETTINGS_OUTPUT_FAILURE, params);
        }
    },

    /**
     * ユーザーのアクセスキー情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} user_id ユーザーのid（主キー）
     * @returns {Promise<void>}
     */
    async access_keys_index({ dispatch, commit }, user_id: number) {
        try {
            commit(types.ACCESS_KEYS_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.User.ACCESS_KEYS.replace(':user_id', user_id.toString()) },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ACCESS_KEYS_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.ACCESS_KEYS_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ユーザーのアクセスキー作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} user_id ユーザーのid
     * @param {string} expiration_at 有効期限
     * @returns {Promise<void>}
     */
    async access_keys_create({ dispatch, commit, rootState }, payload: { user_id: number; expiration_at: string }) {
        try {
            commit(types.CREATE_ACCESS_KEYS_START);
            const params = { expiration_at: payload.expiration_at };
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.User.ACCESS_KEYS.replace(':user_id', payload.user_id.toString()),
                    params: params,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_ACCESS_KEYS_SUCCESS, resData);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.ACCESS_KEY_CREATE);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_ACCESS_KEYS_FAILURE, params);
        }
    },

    /**
     * アクセスキー更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id アクセスキーID
     * @param {number} user_id ユーザーID
     * @param {boolean} enabled 有効状態
     * @returns {Promise<void>}
     */
    async access_keys_update(
        { dispatch, commit, rootState },
        payload: { id: number; user_id: number; enabled: boolean }
    ) {
        try {
            commit(types.UPDATE_ACCESS_KEYS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.User.UPDATE_ACCESS_KEYS.replace(':user_id', payload.user_id.toString()).replace(
                        ':id',
                        payload.id.toString()
                    ),
                    params: { enabled: payload.enabled },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_ACCESS_KEYS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.ACCESS_KEY_UPDATE);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_ACCESS_KEYS_FAILURE, params);
        }
    },

    /**
     * アクセスキー削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} user_id ユーザーID
     * @param {Array<number>} ids アクセスキーID
     * @returns {Promise<void>}
     */
    async access_keys_delete({ dispatch, commit, rootState }, payload: { user_id: number; ids: Array<number> }) {
        try {
            commit(types.DELETE_ACCESS_KEYS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.User.ACCESS_KEYS.replace(':user_id', payload.user_id.toString()),
                    params: { ids: payload.ids },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DELETE_ACCESS_KEYS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.ACCESS_KEY_DELETE);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DELETE_ACCESS_KEYS_FAILURE, params);
        }
    },

    /**
     * 個人設定更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async update_personal_settings({ dispatch, commit, rootState }, payload: PersonalSettings) {
        try {
            commit(types.UPDATE_PERSONAL_SETTINGS_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'put', url: ApiUrl.User.PERSONAL_SETTINGS, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_PERSONAL_SETTINGS_OUTPUT_SUCCESS);

                // 表示言語の変更
                await dispatch('changeDisplayLanguage', { language: payload.language }, { root: true });

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.PERSONAL_SETTING_UPDATE);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_PERSONAL_SETTINGS_OUTPUT_FAILURE, params);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
